import { Validators } from '../../../../utils/validators';
import { FormModel } from '../../../../components/Form';
import { companyCategory, customerType } from '../../../../utils/constants/data';
import { CountryCode } from 'libphonenumber-js';
import { ReactNode } from 'react';

export type AddCustomerFormComponentTypes = {
  strings: any;
  country: [];
  states: [];
  cities: [];
  company: boolean;
  defaultCountry: CountryCode;
  salesCompanies?: any[];
  salesBranches?: any[];
  companyId?: number;
  isSalesManagementEnabled?: boolean;
  defaultCompanyValue?: string; // Default company value from pageResponse state
  isCaptureLoyaltyProgram?: boolean;
  showMembershipFields?: boolean;
  isEditMode?: boolean;
  enableCompanyBranchMappings?: boolean;
  companyBranchMappingSection?: ReactNode;
};
export const AddCustomerForm = ({
  strings,
  country,
  states,
  cities,
  company,
  defaultCountry,
  salesCompanies = [],
  salesBranches = [],
  companyId,
  isSalesManagementEnabled,
  defaultCompanyValue,
  isCaptureLoyaltyProgram = false,
  showMembershipFields = false,
  isEditMode = false,
  enableCompanyBranchMappings = false,
  companyBranchMappingSection,
}: AddCustomerFormComponentTypes): FormModel[] => {
  // Filter out "Individual" option when companyId is present
  const companyOptions = companyId ? [] : customerType(strings);

  const formFields: FormModel[] = [
    {
      label: '',
      value: defaultCompanyValue || '', // Use defaultCompanyValue from pageResponse state
      size: 'medium',
      type: 'radio',
      placeholder: '',
      field: 'company',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { xs: 12 },
      required: true,
      options: companyOptions,
      disabled: !!companyId, // Disable radio when companyId is present to force "Yes"
    },
    {
      label: strings.customermgmt_text_firstName,
      value: '',
      size: 'medium',
      autoFocus: true,
      type: 'text',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'firstName',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateString }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    },
    {
      label: strings.customermgmt_text_lastName,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'lastName',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateString }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    },
    {
      label: strings.customerDetailsEmailId,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'email',
      validators: [
        { check: Validators.validateMandatoryField },
        { check: Validators.validateString },
        { check: Validators.validateEmail },
      ],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    },
    {
      label: strings.customermgmt_text_phone,
      value: '',
      size: 'medium',
      type: 'phoneInput',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'phone',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateMobile }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      defaultCountry: defaultCountry,
    },
    {
      label: strings.customermgmt_text_pinCode,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'pinCode',
      validators: [{ check: Validators.validatePinCode }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
    },
    {
      label: strings.customermgmt_text_address,
      value: '',
      size: 'medium',
      type: 'text',
      placeholder: '',
      field: 'address',
      validators: [{ check: Validators.validateString }],
      responsive: { xs: 12, md: 8, lg: 9 },
      required: false,
    },
  ];

  if (company) {
    if (isSalesManagementEnabled && !enableCompanyBranchMappings) {
      formFields.push(
        {
          label: strings.sales_company_name,
          value: '',
          size: 'medium',
          type: 'drop-down',
          variant: 'outlined',
          placeholder: '',
          field: 'salesCompany',
          validators: [],
          responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
          required: false,
          options: salesCompanies,
          disabled: !!companyId,
        },
        {
          label: strings.sales_company_branch_name,
          value: '',
          size: 'medium',
          type: 'drop-down',
          variant: 'outlined',
          placeholder: '',
          field: 'salesBranch',
          validators: [],
          responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
          required: false,
          options: salesBranches,
          disabled: salesBranches.length === 0 || !!companyId,
        },
      );
    } else if (!isSalesManagementEnabled) {
      formFields.push({
        label: strings.customermgmt_text_company,
        value: '',
        size: 'medium',
        type: 'text',
        variant: 'outlined',
        placeholder: '',
        field: 'companyName',
        validators: [],
        responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
        required: false,
        options: salesCompanies,
        disabled: !!companyId,
      });
    }
    formFields.push({
      label: strings.customermgmt_text_designation,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'designation',
      validators: [{ check: Validators.validateString, max: 50 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
    });
  }

  formFields.push(
    {
      label: strings.customermgmt_text_gst,
      value: '',
      size: 'medium',
      type: 'text',
      placeholder: '',
      field: 'gst',
      validators: [{ check: Validators.validateString, max: 20 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      // disabled: !company,
    },
    {
      label: strings.updateProfile_text_language,
      value: '',
      size: 'medium',
      type: 'select',
      placeholder: strings.updateProfile_text_language,
      field: 'language',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      isMulti: false,
    },
    {
      label: strings.customermgmt_text_country,
      value: '',
      size: 'medium',
      type: 'drop-down',
      placeholder: '',
      field: 'country',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      options: country,
    },
    {
      label: strings.customermgmt_text_state,
      value: '',
      size: 'medium',
      type: 'drop-down',
      placeholder: '',
      field: 'state',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      options: states,
    },
    {
      label: strings.customermgmt_text_city,
      value: '',
      size: 'medium',
      type: 'drop-down',
      placeholder: '',
      field: 'city',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      options: cities,
    },
    {
      label: 'Birth Date',
      value: '',
      size: 'medium',
      type: 'date',
      typeValue: 'date',
      variant: 'outlined',
      placeholder: 'Select birth date',
      field: 'birthDate',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
    },
    {
      label: 'Anniversary Date',
      value: '',
      size: 'medium',
      type: 'date',
      typeValue: 'date',
      variant: 'outlined',
      placeholder: 'Select anniversary date',
      field: 'anniversaryDate',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
    },
  );

  if (company && isSalesManagementEnabled && enableCompanyBranchMappings && companyBranchMappingSection) {
    formFields.push({
      label: '',
      value: '',
      size: 'medium',
      type: 'custom',
      placeholder: '',
      field: 'companyBranchMappingSection',
      validators: [],
      responsive: { xs: 12 },
      required: false,
      children: companyBranchMappingSection,
    });
  }

  // Add membership section if isCaptureLoyaltyProgram is enabled
  if (isCaptureLoyaltyProgram) {
    formFields.push({
      label: 'Add membership details',
      value: false,
      size: 'medium',
      type: 'checkbox',
      placeholder: '',
      field: 'addMembershipDetails',
      validators: [],
      responsive: { xs: 12, md: 12, lg: 12 },
      required: false,
    });

    // Only show membership fields if checkbox is checked
    if (showMembershipFields) {
      formFields.push(
        {
          label: 'Membership Details',
          value: '',
          size: 'medium',
          autoFocus: false,
          type: 'section',
          typeValue: '',
          variant: '',
          placeholder: '',
          field: '',
          validators: [],
          responsive: { xs: 12, md: 12, lg: 12 },
          required: false,
        },
        {
          label: 'Member Name',
          value: '',
          size: 'medium',
          type: 'text',
          typeValue: 'text',
          variant: 'outlined',
          placeholder: 'Enter member name',
          field: 'memberName',
          validators: [
            { check: Validators.validateMandatoryField },
            {
              check: Validators.validateString,
              max: 200,
            },
          ],
          responsive: { xs: 12, sm: 6, md: 3, lg: 3 },
          required: true,
        },
        {
          label: 'Membership number',
          value: '',
          size: 'medium',
          type: 'text',
          typeValue: 'text',
          variant: 'outlined',
          placeholder: 'Membership number',
          field: 'loyaltyReference',
          validators: [
            { check: Validators.validateMandatoryField },
            {
              check: Validators.validateString,
              max: 200,
            },
          ],
          responsive: { xs: 12, sm: 6, md: 3, lg: 3 },
          required: true,
        },
        {
          label: 'Member Type',
          value: '',
          size: 'medium',
          type: 'drop-down',
          variant: 'outlined',
          placeholder: '',
          field: 'memberType',
          validators: [{ check: Validators.validateMandatoryField }],
          responsive: { xs: 12, sm: 6, md: 2, lg: 2 },
          required: true,
          options: companyCategory(),
        },
        {
          label: 'Start Date',
          value: '',
          size: 'medium',
          type: 'date',
          typeValue: 'date',
          variant: 'outlined',
          placeholder: 'Select start date',
          field: 'startDate',
          validators: [{ check: Validators.validateMandatoryField }],
          responsive: { xs: 12, sm: 6, md: 2, lg: 2 },
          required: true,
        },
        {
          label: 'End Date',
          value: '',
          size: 'medium',
          type: 'date',
          typeValue: 'date',
          variant: 'outlined',
          placeholder: 'Select end date',
          field: 'endDate',
          validators: [{ check: Validators.validateMandatoryField }],
          responsive: { xs: 12, sm: 6, md: 2, lg: 2 },
          required: true,
        },
      );
    }
  }

  return formFields;
};
